import React, { useEffect, useState } from 'react';
import { Card, Button, Row, Col, Spinner, InputGroup, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useReservation } from '../components/ReservationContext';
import { api } from '../components/api';
import ROOM_TYPES from '../components/Room_Type';
import { MdMicrowave, MdTv, MdCast, MdAcUnit } from 'react-icons/md';
import { FaMugHot, FaSnowflake, FaWifi } from 'react-icons/fa';
import { FaUser } from 'react-icons/fa';
import StepNavigation from '../components/StepsNavigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation, Pagination } from 'swiper/modules';


export default function Step2_RoomSelection({ setStepNavigationProps }) {
  const { reservation, setReservation } = useReservation();
  const navigate = useNavigate();
  const [availability, setAvailability] = useState([]);
  const [roomSelections, setRoomSelections] = useState({});
  const [selectedRoomType, setSelectedRoomType] = useState('SINGLE'); // default

  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [validationMsg, setValidationMsg] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [roomValidation, setRoomValidation] = useState({});

  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalStartIndex, setModalStartIndex] = useState(0);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [roomModalContent, setRoomModalContent] = useState({ title: '', body: null });


  const adults = reservation.adults;
  const children = reservation.children;
  const totalGuests = adults + children;

  // Calculate nights selected
  const nightsSelected =
    reservation.checkIn && reservation.checkOut
      ? (new Date(reservation.checkOut) - new Date(reservation.checkIn)) / (1000 * 60 * 60 * 24)
      : 0;

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await api.post('/reservation/queryroomsummary', {
          startDate: reservation.checkIn,
          endDate: reservation.checkOut,
        });

        const summary = response.data;

        // Build availability array with per roomType available units and min nights
        const mockAvailability = [
          {
            date: reservation.checkIn,
            availableUnits: summary.reduce((acc, cur) => {
              acc[cur.roomType] = cur.totalAvailable;
              return acc;
            }, {}),
            minimumNights: summary.reduce((acc, cur) => {
              acc[cur.roomType] = cur.minimumNights || 1; // fallback 1 if undefined
              return acc;
            }, {}),
          },
        ];

        const ratesMap = summary.reduce((acc, cur) => {
          acc[cur.roomType] = cur.nightlyRate;
          return acc;
        }, {});

        setAvailability(mockAvailability);
        setRates(ratesMap);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching room availability:', error);
        setLoading(false);
      }
    };

    if (reservation.checkIn && reservation.checkOut) {
      fetchAvailability();
    }
  }, [reservation.checkIn, reservation.checkOut]);

  useEffect(() => {
    setStepNavigationProps({
      onContinue: handleContinue,
      disableContinue: !hasSelectedRooms,
      hideBack: false,
      continueLabel: 'Continue',
      backLabel: 'Back',
      onBack: () => navigate('/')
    });

    return () => {
      setStepNavigationProps({});
    };
  }, [roomSelections, rates, adults, children,
    nightsSelected, setReservation, setStepNavigationProps]);

  // Calculate total base capacity (2 per room regardless of SINGLE/DOUBLE)
  const getTotalBaseCapacity = (selections) => {
    let capacity = 0;
    for (const [roomType, selection] of Object.entries(selections)) {
      if (roomType === 'SINGLE') capacity += selection.count * 2;
      else if (roomType === 'DOUBLE') capacity += selection.count * 2;
      else if (roomType === 'COTTAGE') capacity += selection.count * 7;
    }
    return capacity;
  };

  // Extra charge: $10 per extra person per night
  const calculateExtraPersonCharge = (extraPersons) => {
    const extraChargePerPersonPerNight = 10;
    return extraPersons * extraChargePerPersonPerNight * nightsSelected;
  };

  const validateGuestCapacity = (selections) => {
    let errors = [];

    const singleRooms = selections['SINGLE']?.count || 0;
    const doubleRooms = selections['DOUBLE']?.count || 0;
    const cottageCount = selections['COTTAGE']?.count || 0;

    const totalAdults = adults;
    const totalChildren = children;
    const totalGuests = totalAdults + totalChildren;

    // Max adult capacity: 2 per SINGLE/DOUBLE, 4 per COTTAGE
    const adultCapacity = (singleRooms + doubleRooms) * 2 + (cottageCount * 4);

    // Max total room capacity: 2 per SINGLE, 4 per DOUBLE, 7 per COTTAGE
    const totalRoomCapacity = (singleRooms * 2) + (doubleRooms * 4) + (cottageCount * 7);

    if (totalGuests > totalRoomCapacity) {
      errors.push(`Selected rooms cannot accommodate all ${totalGuests} guests. Please choose more rooms.`);
    }

    if (totalAdults > adultCapacity) {
      errors.push(`For a comfortable and family-friendly stay, we allow a maximum of two adults per room (except cottage allows four). The selected rooms can accommodate up to ${adultCapacity} adults. Please choose more rooms.`);
    }

    if (totalChildren > 0 && totalAdults === 0) {
      errors.push('Children must be accompanied by at least one adult.');
    }

    if (totalAdults === 4 && (singleRooms + doubleRooms + cottageCount) === 1 && !cottageCount) {
      errors.push('For a comfortable and family-friendly stay, 4 adults require at least two rooms. Please select an additional room.');
    }

    return errors;
  };


  // Helper to get minimum nights warning per room type
  const getMinNightsWarning = (roomType) => {
    const minNights = availability[0]?.minimumNights?.[roomType] || 1;
    if (nightsSelected < minNights) {
      if (roomType === 'COTTAGE') {
        return `For Cottage reservations, a minimum of ${minNights} nights is required.`;
      }
      return `You need to choose a minimum of ${minNights} nights for reservation.`;
    }
    return null;
  };

  // Handle + / - buttons for room count
  const handleRoomCountChange = (roomType, delta, availableCount) => {
    setRoomSelections((prev) => {
      const currentCount = prev[roomType]?.count || 0;
      const newCount = currentCount + delta;

      if (newCount < 0) {
        setRoomValidation((prevMsgs) => ({ ...prevMsgs, [roomType]: 'Cannot be less than 0' }));
        return prev;
      }
      if (newCount > availableCount) {
        setRoomValidation((prevMsgs) => ({ ...prevMsgs, [roomType]: 'Not enough rooms available' }));
        return prev;
      }

      setRoomValidation((prevMsgs) => ({ ...prevMsgs, [roomType]: '' }));

      return {
        ...prev,
        [roomType]: {
          count: newCount,
        },
      };
    });
    setValidationMsg('');
    setShowValidation(false);
  };

  // Update reservation with selected rooms and extra charge
  useEffect(() => {
    const selectedRooms = Object.entries(roomSelections)
      .filter(([_, selection]) => selection.count > 0)
      .map(([roomType, selection]) => ({
        roomType,
        count: selection.count,
        rate: rates[roomType],
      }));

    const totalBaseCapacity = getTotalBaseCapacity(roomSelections);
    const extraPersons = Math.max(0, adults + children - totalBaseCapacity);
    const extraCharge = calculateExtraPersonCharge(extraPersons);

    const roomSubtotal = selectedRooms.reduce(
      (sum, room) => sum + room.count * room.rate * nightsSelected,
      0
    );

    let totalBeforeTax = 0;

    if (selectedRooms && selectedRooms.length > 0) {
      totalBeforeTax = roomSubtotal + extraCharge;
    }

    setReservation((prev) => ({
      ...prev,
      rooms: selectedRooms,
      extraCharge,
      totalBeforeTax,
    }));
  }, [roomSelections, rates, adults, children, nightsSelected, setReservation]);

  const hasSelectedRooms = Object.values(roomSelections).some(room => room.count > 0);

  const handleContinue = () => {
    const errors = validateGuestCapacity(roomSelections);
    if (errors.length > 0) {
      setValidationMsg(errors.join(' '));
      setShowValidation(true);
      return;
    }
    navigate('/contact');
  };

  // Sort room types so available rooms appear first.
  // Original ROOM_TYPES order is preserved within available/unavailable groups.
  const sortedRoomTypes = Object.keys(ROOM_TYPES)
    .map((roomType, index) => {
      const availableCount = availability.reduce((min, day) => {
        const available = day.availableUnits[roomType] || 0;
        return Math.min(min, available);
      }, Infinity);

      return {
        roomType,
        availableCount: Number.isFinite(availableCount) ? availableCount : 0,
        originalIndex: index,
      };
    })
    .sort((a, b) => {
      if (a.availableCount > 0 && b.availableCount <= 0) {
        return -1;
      }

      if (a.availableCount <= 0 && b.availableCount > 0) {
        return 1;
      }

      return a.originalIndex - b.originalIndex;
    });

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="p-3">


      <div className="mb-4">
        <h2 className="h5 fw-semibold text-dark mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Great — thanks for sharing your dates!
        </h2>
        <p className="text-muted mb-2" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
Now choose the rooms that fit your group. Scroll to view all available room types.
        </p>
      </div>

      {showValidation && (
        <div
          className="custom-alert-wrapper position-fixed top-50 start-50 translate-middle px-3"
          style={{
            zIndex: 1050,
            width: '100%',
            maxWidth: '400px',
          }}
        >
          <div
            className="alert alert-secondary alert-dismissible fade show text-center shadow"
            role="alert"
            style={{
              backgroundColor: '#212529',
              color: '#fff',
            }}
          >
            {validationMsg}
            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
              onClick={() => setShowValidation(false)}
            ></button>
          </div>
        </div>
      )}

      <Row xs={1} md={2} className="g-4">
        {sortedRoomTypes.map(({ roomType, availableCount }) => {
          const roomInfo = ROOM_TYPES[roomType];
          const normalizedAvailable = availableCount;

          const selection = roomSelections[roomType] || { count: 0 };
          const validation = roomValidation[roomType] || '';
          const minNightsWarning = getMinNightsWarning(roomType);

          return (
            <Col key={roomType}>
              <Card className="border-0 shadow-sm rounded-4 h-100">

                <div className="position-relative">
                  <Swiper
                    modules={[Navigation, Pagination]}
                    navigation
                    pagination={{ clickable: true }}
                    className="rounded-top-4"
                    style={{ height: '240px' }}
                  >
                    {roomInfo.images.map((imgUrl, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={imgUrl}
                          alt={`${roomInfo.name} ${index + 1}`}
                          onClick={() => {
                            setModalImages(roomInfo.images);
                            setModalStartIndex(index);
                            setShowImageModal(true);
                          }}
                          style={{
                            objectFit: 'cover',
                            width: '100%',
                            height: '240px',
                            borderTopLeftRadius: '1rem',
                            borderTopRightRadius: '1rem',
                            cursor: 'pointer'
                          }}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {normalizedAvailable > 0 ? (
                    <span className="badge bg-success position-absolute top-0 end-0 m-2">
                      {normalizedAvailable} Available
                    </span>
                  ) : (
                    <>
                      <span className="badge bg-danger position-absolute top-0 end-0 m-2">
                        Unavailable
                      </span>
                      <div
                        className="position-absolute inset-0"
                        style={{ backgroundColor: 'rgba(255,255,255,0.6)', pointerEvents: 'none', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}
                      />
                    </>
                  )}
                </div>

                <Card.Body className="d-flex flex-column">
                  <div className="flex-grow-1">
                    <Card.Title className="fw-semibold mb-2">{roomInfo.name}</Card.Title>
                    <Card.Text className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: roomInfo.description }} />

                    <div className="mb-2">
                      <span className="fw-semibold text-dark"> <a
                        href="#"
                        className="small text-decoration-underline"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedRoomType(roomType);
                          setRoomModalContent({});
                          setShowRoomModal(true);
                        }}
                      >
                        More details
                      </a></span>
                    </div>

                    {roomType === 'SINGLE' && (
                      <Card.Text className="text-muted small">Max 2 guests</Card.Text>
                    )}
                    {roomType === 'DOUBLE' && (
                      <Card.Text className="text-muted small">Max 4 guests (Max 2 adults, Max 3 children)</Card.Text>
                    )}

                    {roomType === 'COTTAGE' && (
                      <Card.Text className="text-muted small">Max 4-5 guests</Card.Text>
                    )}

                    {minNightsWarning && (
                      <Card.Text className="text-danger small mt-1">
                        {minNightsWarning}
                      </Card.Text>
                    )}

                    <div className="mb-2">
                      {normalizedAvailable > 0 ? (
                        <>
                          {roomType === 'COTTAGE' && (
                            <div className="mt-1 mb-3">
                              <div className="d-flex align-items-center gap-2 small text-dark">
<span className="fw-semibold">
  <span style={{ fontSize: '0.75em', verticalAlign: 'text-top', color: '#6c757d', marginRight: '2px' }}>
    CAD
  </span>
  {rates[roomType]}/night
</span>

                              </div>
                            </div>
                          )}

                          {roomType === 'SINGLE' && (
                            <div className="mt-1 mb-3">
                              <div className="d-flex align-items-center gap-2 small text-dark">
                                {Array.from({ length: 2 }).map((_, i) => (
                                  <FaUser key={i} size={13} />
                                ))}
<span className="fw-semibold">
  <span style={{ fontSize: '0.75em', verticalAlign: 'text-top', color: '#6c757d', marginRight: '2px' }}>
    CAD
  </span>
  {rates[roomType]}/night
</span>

                              </div>
                            </div>
                          )}

                          {roomType === 'DOUBLE' && (
                            <div className="mt-1 mb-3">
                              {[2, 3, 4].map((count) => {
                                const extraPeople = count - 2;
                                const price = rates[roomType] + extraPeople * 10;
                                return (
                                  <div
                                    key={count}
                                    className="d-flex align-items-center small text-dark"
                                    style={{
                                      justifyContent: 'space-between',
                                      width: '150px',
                                      lineHeight: '1.4',
                                    }}
                                  >
                                    <div className="d-flex align-items-center" style={{ gap: '2px' }}>
                                      {Array.from({ length: count }).map((_, i) => (
                                        <FaUser key={i} size={13} />
                                      ))}
                                    </div>
                                    <span
                                      className="fw-semibold text-dark"
                                      style={{ marginLeft: '8px', textAlign: 'right' }}
                                    >
                                      <span style={{ fontSize: '0.75em', verticalAlign: 'text-top', color: '#6c757d', marginRight: '2px' }}>
    CAD
  </span>{price}/night
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="mt-2 mb-3">
                          <span className="text-muted small">Price: <strong>N/A</strong></span>
                        </div>
                      )}
                    </div>

                   
                  </div>

                  {/* Room selector and availability aligned at bottom */}
                  {normalizedAvailable > 0 ? (
                    <div className="d-flex flex-column align-items-center mt-auto">
                      {/* Add message above the + and - buttons */}
<div className="text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: 600, color: '#198754' }}>
  <span style={{ marginRight: '4px' }}>We have</span>
  <span
    style={{
      fontWeight: 800,
      fontSize: '1.2em',
      padding: '2px 6px',
      borderRadius: '6px',
      color: '#000',
      backgroundColor: 'rgba(25,135,84,0.15)'
    }}
  >
    {normalizedAvailable}
  </span>
  <span style={{ marginLeft: '4px' }}> left</span>
</div>

                      <InputGroup style={{ maxWidth: '130px' }}>
                        <Button
                          variant="outline-secondary"
                          style={{
    fontWeight: 700,
    color: '#000',
    borderColor: '#000'
  }}
                          disabled={selection.count <= 0}
                          onClick={() => handleRoomCountChange(roomType, -1, normalizedAvailable)}
                        >
                          -
                        </Button>
                        <Form.Control
                          readOnly
                          value={selection.count}
                          className="text-center"
                          aria-label={`${roomType} room count`}
                        />
                        <Button
                          variant="outline-secondary"
                          style={{
    fontWeight: 700,
    color: '#000',
    borderColor: '#000'
  }}
                          disabled={selection.count >= normalizedAvailable}
                          onClick={() => handleRoomCountChange(roomType, 1, normalizedAvailable)}
                        >
                          +
                        </Button>
                      </InputGroup>
                      <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
  Select rooms using + or − above
</div>
                      {validation && <div className="text-danger small mt-1">{validation}</div>}

                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center mt-auto">
                      <span className="badge bg-danger mt-2">Not Available</span>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {showImageModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 1060 }}
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="position-relative w-100 h-100"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="light"
              size="sm"
              className="position-absolute top-0 end-0 m-3"
              onClick={() => setShowImageModal(false)}
              style={{
                borderRadius: '50%',
                fontWeight: 'bold',
                zIndex: 1100
              }}
            >
              &times;
            </Button>

            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              initialSlide={modalStartIndex}
              style={{ height: '100%', width: '100%' }}
            >
              {modalImages.map((img, i) => (
                <SwiperSlide key={i}>
                  <img
                    src={img}
                    alt={`Modal Slide ${i + 1}`}
                    className="img-fluid"
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}

      {showRoomModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1060 }}
          onClick={() => setShowRoomModal(false)}
        >
          <div
            className="bg-white rounded-4 shadow p-4 position-relative"
            style={{ width: '90%', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="light"
              size="sm"
              className="position-absolute top-0 end-0 m-2"
              onClick={() => setShowRoomModal(false)}
              style={{ borderRadius: '50%' }}
            >
              &times;
            </Button>

            {selectedRoomType === 'COTTAGE' ? (
              <>
                <h5 className="fw-bold mb-3">About Cottage</h5>
                <Card.Text className="text-muted">
                  Our second-floor cottage is thoughtfully designed to provide both comfort and space for up to <strong>4-5 guests</strong>. It features <strong>2 bedrooms with 3 queen beds and one single size bed</strong>, a <strong>full private washroom</strong> are stocked with
  <strong> fresh linens and clean towels</strong> for your comfort.<br /><br />
 
 It has <strong>private kitchenette</strong> equipped with a
  <strong> refrigerator</strong>, <strong>microwave</strong>, and
  <strong> coffee maker</strong>, along with <strong>air conditioning</strong> 
  <br/>

                  Enjoy a spacious and inviting <strong>living room</strong> equipped with a cozy <strong>sofa, chairs</strong>, and a <strong>Smart TV</strong>—perfect for movie nights or relaxing with family and friends.<br /><br />

                  The cottage includes a dedicated <strong>office workspace</strong>, ideal for remote workers or digital nomads needing a quiet and productive environment.<br /><br />

                  For year-round comfort, relax in the <strong>fully enclosed porch</strong> with both <strong>air conditioning</strong> and <strong>heating</strong>. It’s a great spot to read, work, or enjoy your morning coffee.<br /><br />

                  <strong>Please note:</strong> This cottage is located on the <strong>second floor</strong>, offering privacy and a quiet retreat away from ground-level activity. It <strong>does not have stove</strong><br /><br />

                  <span className="badge bg-success me-2">
                    <i className="bi bi-tree-fill me-1"></i> Dedicated Outdoor Sitting Area
                  </span>
                  <em>Step outside to a private outdoor seating space—perfect for meals, morning coffee, or evening relaxation under the sky.</em><br /><br />

                  <span className="badge bg-warning text-dark me-2">
                    <i className="bi bi-fire me-1"></i> Private BBQ with Free Propane
                  </span>
                  <em>Enjoy grilling with your own dedicated BBQ grill—free propane included for your stay.</em><br /><br />

                  <span className="badge bg-success me-2">
                    <i className="bi bi-sun-fill me-1"></i> Family & Group Friendly
                  </span>
                  <em>Spacious layout ideal for families, work retreats, or groups traveling together.</em><br /><br />

                  <span className="badge bg-info text-dark me-2">
                    <i className="bi bi-wifi me-1"></i> Work From Home Ready
                  </span>
                  <em>High-speed internet and a dedicated office area support your remote work needs.</em><br /><br />
                  <span className="badge bg-info text-dark me-2">
                    <i className="bi bi-shield-check me-1"></i> Service Animals Welcome
                  </span>
                  <em>While we are not a pet-friendly property, certified service animals — especially service dogs — are always welcome at no additional charge.</em><br /><br />

                  <span className="badge bg-danger me-2">
                    <i className="bi bi-slash-circle me-1"></i> Non-Smoking Cottage
                  </span>
                  <em>This unit is strictly non-smoking to ensure a clean and healthy environment for all guests.</em>
                </Card.Text>

              </>
            ) : (

              <>
                <h5 className="fw-bold mb-3">About Rooms</h5>
                <Card.Text className="text-muted">
                  All our rooms are thoughtfully designed for comfort and convenience.
                  Each includes a <strong>microwave</strong>, <strong>refrigerator</strong>,
                  <strong> coffee maker</strong>, <strong>air conditioning</strong>, and <strong>heating</strong> to ensure a
                  pleasant stay in any season.<br /><br />
                  For entertainment, you'll enjoy <strong>high-speed fiber optic internet</strong> and
                  a <strong>Smart TV</strong> with a wide variety of streaming options.<br /><br /> 
                  All rooms feature a <strong>private washroom</strong> and are stocked with
  <strong> fresh linens and clean towels</strong> for your comfort.<br /><br />
                  We also take pride in offering <strong>elderly-friendly washroom</strong> and ensuring that
                  <strong> all rooms are located at ground level</strong> for easy access.<br /><br />

                  <span className="badge bg-success me-2">
                    <i className="bi bi-sun-fill me-1"></i> Summer Special
                  </span>
                  <em>Enjoy free outdoor BBQ and complimentary campfire wood — perfect for cozy evenings under the stars.</em><br /><br />

                  <span className="badge bg-info text-dark me-2">
                    <i className="bi bi-shield-check me-1"></i> Service Animals Welcome
                  </span>
                  <em>While we are not a pet-friendly property, certified service animals — especially service dogs — are always welcome at no additional charge.</em><br /><br />

                  <span className="badge bg-danger me-2">
                    <i className="bi bi-slash-circle me-1"></i> Non-Smoking Rooms
                  </span>
                  <em>All rooms are strictly non-smoking to ensure a clean and healthy environment for all guests.</em>
                </Card.Text>

              </>
            )}

          </div>
        </div>
      )}

    </div>
  );
}
